import type { Table } from '@tanstack/react-table';
import { List } from '@nexelem/components';
import { SpecialColumnsIds } from '../SpecialColumns';

type DragEndEvent = Parameters<typeof List.move>[1];

export const ColumnOrderGroups = {
  left: 'left',
  center: 'center',
  right: 'right',
} as const;

export type ColumnOrderGroup = (typeof ColumnOrderGroups)[keyof typeof ColumnOrderGroups];

export type ColumnListItem<TData> = {
  column: ReturnType<Table<TData>['getLeftLeafColumns']>[number];
  index: number;
  group: ColumnOrderGroup;
};

export const getColumnListItems = <TData>(table: Table<TData>): ColumnListItem<TData>[] => {
  const left = table.getLeftLeafColumns().map((column, index) => {
    return { column, index, group: ColumnOrderGroups.left };
  });
  const center = table.getCenterLeafColumns().map((column, index) => {
    return { column, index, group: ColumnOrderGroups.center };
  });
  const right = table.getRightLeafColumns().map((column, index) => {
    return { column, index, group: ColumnOrderGroups.right };
  });

  return [...left, ...center, ...right].filter(
    (column) => column.column.id !== SpecialColumnsIds.ACTIONS && column.column.id !== SpecialColumnsIds.STATE,
  );
};

export const reorderColumns = <TData>(table: Table<TData>, event: DragEndEvent) => {
  const groups = {
    left: table.getLeftLeafColumns().map((column) => column.id),
    center: table.getCenterLeafColumns().map((column) => column.id),
    right: table.getRightLeafColumns().map((column) => column.id),
  };

  const next = List.move(groups, event);

  // Cross-group moves must not change pin membership — only reorder within a group.
  if (
    next.left.length !== groups.left.length ||
    next.center.length !== groups.center.length ||
    next.right.length !== groups.right.length
  ) {
    return;
  }

  table.setColumnOrder([...next.left, ...next.center, ...next.right]);
  table.setColumnPinning({ left: next.left, right: next.right });
};
